import { STATIC_IMAGE_BASE } from 'libs/constants'
import type { SkillCategory } from '../../types'

export const SKILL_CATEGORIES: SkillCategory[] = [
  {
    id: 1,
    name: 'Leadership',
    fullName: 'Leadership',
    slug: 'leadership',
    description:
      'Help your employees develop key leadership traits to become better coaches, motivators, and strategists that drive impact across your organization.',
    pageData: {
      id: 1,
      iconName: 'IconFlagBearer',
      audienceHeadline1:
        'For aspiring managers starting their leadership journey',
      audienceHeadline2:
        'For first-time managers advancing their leadership abilities',
      audienceHeadline3:
        'For executives building a strategic vision and culture',
    },
    pillars: [
      {
        id: 1,
        name: 'How to communicate vision and generate buy-in',
        samples: [
          {
            id: 1,
            title: 'Good communication goes two ways',
            description:
              'Former CEO of the Walt Disney Co. Bob Iger shares how he communicates and collaborates with his team to build trust and generate buy-in on key strategic initiatives',
            skills: ['Strategy', 'Collaboration'],
            course: {
              id: 145,
              slug: 'bob-iger-teaches-business-strategy-and-leadership',
              headline: 'Teaches Business Strategy and Leadership',
              instructorName: 'Bob Iger',
              enterpriseBio:
                'Former CEO of The Walt Disney Company teaches strategic leadership ',
              skill: 'Strategy',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/75S2862wjPVBoW3wjhCHEfDf',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/tC5PsLqqDi4YzVX2XjGumNbM',
            },
            video16x9: {
              id: 5070,
              title: 'Good communication goes two ways',
              mediaUuid: '6315122959112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/ggvy8jsbuxa7earsymhi3uvi0opd',
            },
          },
          {
            id: 2,
            title: 'Self knowledge is key',
            description:
              'Emmy winner LeVar Burton explains how the most effective storytellers work to develop a deep understanding of self that helps them tell more authentic, impactful stories',
            skills: ['Storytelling', 'Self-Awareness'],
            course: {
              id: 222,
              slug: 'levar-burton-teaches-the-power-of-storytelling',
              headline: 'Teaches the Power of Storytelling',
              instructorName: 'LeVar Burton',
              enterpriseBio:
                'Emmy winner and Reading Rainbow host LeVar Burton teaches his techniques for dynamic storytelling.',
              skill: 'Storytelling',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/j0dsfu4i07yv1cpj3ouxsg52zd14',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/4ktwwd7i0sr37ffsqva3syiy2gd4',
            },
            video16x9: {
              id: 3377,
              mediaUuid: '6265612623001',
              title: 'Self knowledge is key',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/yhu7jzxxnhcv6k5mjsuslmbxel51',
            },
          },
        ],
      },
      {
        id: 2,
        name: 'How to build inclusive and collaborative teams',
        samples: [
          {
            id: 3,
            title: "Make room for failure's lessons",
            description:
              'Pulitzer Prize winner and presidential biographer Doris Kearns Goodwin explains why leaders need to give people room to fail to encourage risk taking, bring out the best ideas, and find the best path forward.',
            skills: ['Building Trust', 'Empowerment'],
            course: {
              id: 146,
              slug: 'doris-kearns-goodwin-teaches-us-presidential-history-and-leadership',
              headline: 'Teaches U.S. Presidential History and Leadership',
              instructorName: 'Doris Kearns Goodwin',
              enterpriseBio:
                'Pulitzer prize winning biographer teaches leading with emotional intelligence',
              skill: 'Emotional Intelligence',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/2G4hcaWEGsjD6rshZDCJWVE6',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/jYMC16ENoGmwwYDfp5wZ9JVT',
            },
            video16x9: {
              id: 5075,
              title: "Make room for failure's lessons",
              mediaUuid: '6315633323112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/gpwwcho97fcp4njym2kkm9tp78mu',
            },
          },
          {
            id: 4,
            title: 'What does "culture" mean anyway?',
            description:
              'Former Starbucks CEO describes his definition of company culture and the importance for leaders to help others believe they are part of something bigger than themselves.',
            skills: ['Team Building', 'Belonging'],
            course: {
              id: 96,
              slug: 'howard-schultz-leading-a-values-based-business',
              headline: 'Business Leadership',
              instructorName: 'Howard Schultz',
              enterpriseBio:
                'Former CEO of Starbucks teaches how to build a strong company culture',
              skill: 'Strengthening Culture',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/qT5QwPoamnqVHXeaV8ppu5ad',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/8UJMf2tJJL8vyqG8ivq72YWe',
            },
            video16x9: {
              id: 2575,
              mediaUuid: '6238022639001',
              title: 'What does "culture" mean anyway?',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/6HnWMY2WgGP6X31SkBiDRLWY',
            },
          },
        ],
      },
      {
        id: 3,
        name: 'How to deliver effective feedback and coaching',
        samples: [
          {
            id: 5,
            title: 'Trust is everything',
            description:
              'Legendary basketball coach explains why the foundation of effective feedback is trust, and that people are more likely to respond to feedback when they trust the person delivering that message.',
            skills: ['Coaching', 'Building Trust'],
            course: {
              id: 261,
              slug: 'geno-auriemma-teaches-leading-winning-teams',
              headline: 'Teaches Leading Winning Teams',
              instructorName: 'Geno Auriemma',
              enterpriseBio:
                'Champion coach teaches how to motivate your team through effective feedback',
              skill: 'Coaching',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/nzet1zuv7vr6p3yxx0y1s9cjcuz7',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/eva296ye1vsbpujpfbkj9f7iudfm',
            },
            video16x9: {
              id: 5081,
              title: 'Trust is everything',
              mediaUuid: '6316748854112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/ss3r1jsbzvpbgor66qgt952h3lrf',
            },
          },
          {
            id: 6,
            title: 'Working with others',
            description:
              'Trailblazing executive & former PepsiCo CEO explains why giving clear, concise feedback that helps others at the company advance in their own careers is the most important job of a great leader.',
            skills: ['Feedback', 'Clarity'],
            course: {
              id: 390,
              slug: 'indra-nooyi-teaches-leading-with-purpose',
              headline: 'Teaches Leading With Purpose',
              instructorName: 'Indra Nooyi',
              enterpriseBio:
                'Former CEO of PepsiCo teaches her seven Cs of leadership',
              skill: 'Building Trust',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/2sck32hnw3sbi3vvlnbg9gvhxly5',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/4i1q7vav3io8hshkdksjmzzjodkl',
            },
            video16x9: {
              id: 5079,
              title: 'Working with others',
              mediaUuid: '6315334905112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/hn43alfem7hk6hv27plmf5fkwcqu',
            },
          },
        ],
      },
    ],
    targetSkills: [
      {
        id: 1,
        name: 'Bias',
      },
      {
        id: 2,
        name: 'Coaching',
      },
      {
        id: 3,
        name: 'Confidence',
      },
      {
        id: 4,
        name: 'Courage',
      },
      {
        id: 5,
        name: 'Decision Making',
      },
      {
        id: 6,
        name: 'Empathy',
      },
      {
        id: 7,
        name: 'Empowerment',
      },
      {
        id: 8,
        name: 'Integrity',
      },
      {
        id: 9,
        name: 'Motivation',
      },
      {
        id: 10,
        name: 'Persistence',
      },
      {
        id: 11,
        name: 'Team Building',
      },
      {
        id: 12,
        name: 'Vision',
      },
      {
        id: 13,
        name: 'Vulnerability',
      },
    ],
    featuredCourses: [
      {
        id: 145,
        slug: 'bob-iger-teaches-business-strategy-and-leadership',
        instructorName: 'Bob Iger',
        enterpriseBio:
          'Former CEO of The Walt Disney Company teaches strategic leadership',
        skill: 'Strategy',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/75S2862wjPVBoW3wjhCHEfDf',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/tC5PsLqqDi4YzVX2XjGumNbM',
        courseUrl:
          'https://work.masterclass.com/bob-iger-teaches-business-strategy-and-leadership',
        skillDescription: 'Business Strategy and Leadership',
      },
      {
        id: 384,
        slug: 'richard-branson-teaches-disruptive-entrepreneurship',
        instructorName: 'Richard Branson',
        enterpriseBio:
          'Entrepeneur and founder of the Virgin Group teaches leading a team',
        skill: 'Team Buiding',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/fjgg118qpjlxh0and6f04a4ff0am',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/mbbnnk88uvvffe2jyi0yka1s7rzg',
        courseUrl:
          'https://work.masterclass.com/richard-branson-teaches-disruptive-entrepreneurship',
        skillDescription: 'Disruptive Entrepreneurship',
      },
      {
        id: 390,
        slug: 'indra-nooyi-teaches-leading-with-purpose',
        instructorName: 'Indra Nooyi',
        enterpriseBio:
          'Former CEO of PepsiCo teaches her seven Cs of leadership',
        skill: 'Building Trust',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/2sck32hnw3sbi3vvlnbg9gvhxly5',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/4i1q7vav3io8hshkdksjmzzjodkl',
        courseUrl:
          'https://work.masterclass.com/indra-nooyi-teaches-leading-with-purpose',
        skillDescription: 'Leading With Purpose',
      },
      {
        id: 112,
        slug: 'sara-blakely-teaches-self-made-entrepreneurship',
        instructorName: 'Sara Blakely',
        enterpriseBio:
          'Entrepeneur and founder of Spanx teaches how to bring a vision to market',
        skill: 'Vision',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/dNT3tuMbPkbwf4r212iaj23d',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/JXEKTHVxZbZuCQLjBqAmFgjU',
        courseUrl:
          'https://work.masterclass.com/sara-blakely-teaches-self-made-entrepreneurship',
      },
      {
        id: 308,
        slug: 'president-bill-clinton-teaches-inclusive-leadership',
        instructorName: 'Bill Clinton',
        enterpriseBio:
          'Former US President teaches decision making and empowering your team',
        skill: 'Decision Making',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/1slbgdi43cpmnwdkbt7vioc5ihgx',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/4d8wbfq4oj8v9nd0gg3k6awh58m8',
        courseUrl:
          'https://www.masterclass.com/classes/president-bill-clinton-teaches-inclusive-leadership',
      },
      {
        id: 385,
        slug: 'president-george-w-bush-teaches-authentic-leadership',
        instructorName: 'George W. Bush',
        enterpriseBio:
          'Former US President teaches about accountabiity when leading a team',
        skill: 'Team Buiding',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/49cihxh466kg5ryi4izypxg4zf3j',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/hhh1ny3vujenc9h5kt3xol4byu04',
        courseUrl:
          'https://www.masterclass.com/classes/president-george-w-bush-teaches-authentic-leadership',
      },
      {
        id: 96,
        slug: 'howard-schultz-leading-a-values-based-business',
        instructorName: 'Howard Schultz',
        enterpriseBio:
          'Former CEO of Starbucks teaches how to build a strong company culture',
        skill: 'Strengthening Culture',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/qT5QwPoamnqVHXeaV8ppu5ad',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/8UJMf2tJJL8vyqG8ivq72YWe',
        courseUrl:
          'https://www.masterclass.com/classes/howard-schultz-leading-a-values-based-business',
      },
      {
        id: 261,
        slug: 'geno-auriemma-teaches-leading-winning-teams',
        instructorName: 'Geno Auriemma',
        enterpriseBio:
          'Champion coach teaches how to motivate your team through effective feedback',
        skill: 'Coaching',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/nzet1zuv7vr6p3yxx0y1s9cjcuz7',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/eva296ye1vsbpujpfbkj9f7iudfm',
        courseUrl:
          'https://work.masterclass.com/geno-auriemma-teaches-leading-winning-teams',
      },
      {
        id: 109,
        slug: 'anna-wintour-teaches-creativity-and-leadership',
        instructorName: 'Anna Wintour',
        enterpriseBio:
          'Editor-in-Chief of Vogue teaches how to build and execute your creative vision',
        skill: 'Vision',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/GxGsJgXH8Pr8nRBhCyJmDyMw',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/QY1sxrpyv2TX98u9NQkoUjSp',
        courseUrl:
          'https://work.masterclass.com/anna-wintour-teaches-creativity-and-leadership',
      },
      {
        id: 146,
        slug: 'doris-kearns-goodwin-teaches-us-presidential-history-and-leadership',
        instructorName: 'Doris Kearns Goodwin',
        enterpriseBio:
          'Pulitzer prize winning biographer teaches leading with emotional intelligence',
        skill: 'Emotional Intelligence',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/2G4hcaWEGsjD6rshZDCJWVE6',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/jYMC16ENoGmwwYDfp5wZ9JVT',
        courseUrl:
          'https://www.masterclass.com/classes/doris-kearns-goodwin-teaches-us-presidential-history-and-leadership',
      },
      {
        id: 151,
        slug: 'neil-degrasse-tyson-teaches-scientific-thinking-and-communication',
        instructorName: 'Neil deGrasse Tyson',
        enterpriseBio:
          'Astrophysicist teaches about awareness of bias in decision making',
        skill: 'Unconscious Bias',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/oAnaDAmeZeBrJ9Azj66JEfeF',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/WvQD9S1Nmppijiqhmm3c1yNv',
        courseUrl:
          'https://www.masterclass.com/classes/neil-degrasse-tyson-teaches-scientific-thinking-and-communication',
      },
      {
        id: 222,
        slug: 'levar-burton-teaches-the-power-of-storytelling',
        instructorName: 'LeVar Burton',
        enterpriseBio:
          'Emmy winner teaches techniques for more effective, impactful storytelling',
        skill: 'Storytelling',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/j0dsfu4i07yv1cpj3ouxsg52zd14',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/4ktwwd7i0sr37ffsqva3syiy2gd4',
        courseUrl:
          'https://www.masterclass.com/classes/levar-burton-teaches-the-power-of-storytelling',
      },
      {
        id: 198,
        slug: 'daniel-pink-teaches-sales-and-persuasion',
        instructorName: 'Daniel Pink',
        enterpriseBio:
          'Best-selling author teaches the science of persuasion and motivating others',
        skill: 'Persuasion',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/r9Gc7uRtPAeQwZG9F7g7o64K',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/AtwHNPAexR65hmRapZyinNeM',
        courseUrl:
          'https://work.masterclass.com/daniel-pink-teaches-sales-and-persuasion',
      },
    ],
  },
  {
    id: 2,
    name: 'DEI',
    fullName: 'Diversity, Equity, and Inclusion',
    slug: 'dei',
    description:
      'Help your teams develop greater empathy, bias awareness, and emotional intelligence to foster a more inclusive workplace culture.',
    pageData: {
      id: 2,
      iconName: 'IconNotebookWriting',
      audienceHeadline1:
        'For leaders building a more supportive workplace culture',
      audienceHeadline2: 'For individual contributors working on diverse teams',
      audienceHeadline3:
        'For sales and customer-facing teams operating in different markets',
    },
    pillars: [
      {
        id: 4,
        name: 'How to create integrated and inclusive organizations',
        samples: [
          {
            id: 7,
            title: 'Respect and reconciliation',
            description:
              'Former Secretaries of State Madeleine Albright and Condoleeza Rice explain the importance of respecting difference rather than tolerating difference, and how to work together to find a common path forward.',
            skills: ['Collaboration', 'Belonging'],
            course: {
              id: 386,
              slug: 'madeleine-albright-and-condoleezza-rice-teach-diplomacy',
              headline: 'Teach Diplomacy',
              instructorName: 'Madeline Albright & Condolezza Rice',
              enterpriseBio:
                'Former secrataries of state teach about diplomacy and conflict resolution',
              skill: 'Cross-Cultural Communication',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/brgiu0b7l68dc0lwqwxelrvl4yrq',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/4kzim7o3abp6ic9sofd4t0tguwyi',
            },
            video16x9: {
              id: 5076,
              title: 'Respect and reconciliation',
              mediaUuid: '6315637870112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/zfkwpn21vr7yc0qjy40rs4wky3na',
            },
          },
          {
            id: 8,
            title: 'Empathy helps with healing',
            description:
              'Fitness expert and Peleton Head instructor Robin Arzon explains why supporting others through empathy can create a calming effect and empower them through challenging times.',
            skills: ['Empowerment', 'Empathy'],
            course: {
              id: 223,
              slug: 'robin-arzon-teaches-mental-strength',
              headline: 'Teaches Mental Strength',
              instructorName: 'Robin Arzon',
              enterpriseBio:
                'Fitness expert teaches resilience and overcoming mental blocks',
              skill: 'Resilience',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/t592pxqdyln3o1fk7ykdo8q8bfn7',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/65ddpr3hyjqir1vjieryk4p6flvl',
            },
            video16x9: {
              id: 5069,
              title: 'Empathy helps with healing',
              mediaUuid: '6315638320112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/7cuwg2928mw1p9q0jt6n5ceuzeue',
            },
          },
        ],
      },
      {
        id: 5,
        name: 'How to communicate across cultures and perspectives',
        samples: [
          {
            id: 9,
            title: 'Kitchen table mediation',
            description:
              'Writer, activist and facilitator Adrienne Maree Brown shares the "Kitchen Table Mediation" technique to help resolve conflict through intimate listening and communication.',
            skills: ['Conflict Resolution', 'Listening'],
            course: {
              id: 268,
              slug: 'redefining-feminism-with-gloria-steinem-and-noted-co-instructors',
              headline: 'Gloria Steinem and Noted Co-Instructors',
              instructorName: 'Gloria Steinem',
              enterpriseBio:
                'Journalist/activists teach about fostering belonging among diverse teams',
              skill: 'Belonging',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/v4xmta2tc07l9nawphznacg0lhwi',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/rdrsng4qsbhn8580d5el9v0uqzvv',
            },
            video16x9: {
              id: 5074,
              title: 'Kitchen table mediation',
              mediaUuid: '6315634545112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/jflg7xrdxze5o227wx34oqnaamgn',
            },
          },
          {
            id: 10,
            title: 'No such thing as homogeneous',
            description:
              'Distinguished philosopher Cornel West explains the importance of being conscious of historical constructs while still recognizing and honoring the individuality of each person.',
            skills: ['Cultural Awareness', 'Empathy'],
            course: {
              id: 269,
              slug: 'cornel-west-teaches-philosophy',
              headline: 'Teaches Philosophy',
              instructorName: 'Cornel West',
              enterpriseBio:
                'Philosopher and activist teaches about love, compassion, and connection',
              skill: 'Belonging',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/nyx8hko1uaoqahd7i735zs7zaa08',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/ulz0qrqtr10diyf927a5jgsudqdd',
            },
            video16x9: {
              id: 3807,
              title: 'No such thing as homogeneous',
              mediaUuid: '6281645405001',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/g8i1l2f4nn9lu6uvo8uufnzim53z',
            },
          },
        ],
      },
      {
        id: 6,
        name: 'How to reflect on one’s own identity and cultural lens',
        samples: [
          {
            id: 11,
            title: 'Alternative resume',
            description:
              'Renowned psycotherapist Esther Perrel explain her concept of an "alternative resume" and how understanding past experiences and relationship are critical to developing relational intelligence.',
            skills: ['Self-Awareness', 'Emotional Intelligence'],
            course: {
              id: 389,
              slug: 'esther-perel-teaches-relational-intelligence',
              headline: 'Teaches Relational Intelligence',
              instructorName: 'Esther Perrel ',
              enterpriseBio:
                'Renowned psychotherapist teaches connecting with others meaningfully',
              skill: 'Emotional Intelligence',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/d2cf6y8gzvnau3et6lo99ydl1cvs',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/11ozvgl1uv33o0nifz82l7fk1oe5',
            },
            video16x9: {
              id: 5068,
              title: 'Alternative resume',
              mediaUuid: '6309699285112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/i31mqxbblnht9llcw7vw6qxcnx72',
            },
          },
          {
            id: 12,
            title: 'We only see what we want to see',
            description:
              'Renowned astrophysicist Neil DeGrasse Tyson describes the different types of biases and how using scientific thinking can help avoid bias in decision making.',
            skills: ['Avoiding Bias', 'Self-Awareness'],
            course: {
              id: 151,
              slug: 'neil-degrasse-tyson-teaches-scientific-thinking-and-communication',
              headline: 'Teaches Scientific Thinking and Communication',
              instructorName: 'Neil deGrasse Tyson',
              enterpriseBio:
                'Astrophysicist teaches about awareness of bias in decision making',
              skill: 'Unconscious Bias',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/oAnaDAmeZeBrJ9Azj66JEfeF',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/WvQD9S1Nmppijiqhmm3c1yNv',
            },
            video16x9: {
              id: 5077,
              title: 'We only see what we want to see',
              mediaUuid: '6315633206112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/02f7avxh0d8jz3fsyos9ilm5pjtz',
            },
          },
        ],
      },
    ],
    targetSkills: [
      {
        id: 14,
        name: 'Avoiding Bias',
      },
      {
        id: 15,
        name: 'Belonging',
      },
      {
        id: 16,
        name: 'Collaboration',
      },
      {
        id: 17,
        name: 'Conflict Resolution',
      },
      {
        id: 18,
        name: 'Cultural Awareness',
      },
      {
        id: 19,
        name: 'Emotional Intelligence',
      },
      {
        id: 6,
        name: 'Empathy',
      },
      {
        id: 7,
        name: 'Empowerment',
      },
      {
        id: 20,
        name: 'Feminism',
      },
      {
        id: 21,
        name: 'Listening',
      },
      {
        id: 22,
        name: 'Psychological Safety',
      },
      {
        id: 23,
        name: 'Self-Awareness',
      },
      {
        id: 24,
        name: 'Unconscious Bias',
      },
    ],
    featuredCourses: [
      {
        id: 268,
        slug: 'redefining-feminism-with-gloria-steinem-and-noted-co-instructors',
        instructorName: 'Gloria Steinem',
        enterpriseBio:
          'Journalist/activists teach about fostering belonging among diverse teams',
        skill: 'Belonging',
        instructorHeadshotUrl: `${STATIC_IMAGE_BASE}/gs_instructor_headshot.Jpg`,
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/rdrsng4qsbhn8580d5el9v0uqzvv',
        courseUrl:
          'https://www.masterclass.com/classes/redefining-feminism-with-gloria-steinem-and-noted-co-instructors',
        skillDescription: 'Redefining Feminism',
      },
      {
        id: 306,
        slug: 'lewis-hamilton-teaches-a-winning-mindset',
        instructorName: 'Lewis Hamilton ',
        enterpriseBio:
          'World racing champion teaches empowering under-represented groups for success',
        skill: 'Cultural Awareness',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/hlwgkd2mibwqyt99y72cwhqzlu3j',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/ilnrs0iq099lyds0wocosepo14c9',
        courseUrl:
          'https://www.masterclass.com/classes/lewis-hamilton-teaches-a-winning-mindset',
        skillDescription: 'A Winning Mindset',
      },
      {
        id: 267,
        slug: 'the-power-of-empathy-with-pharrell-williams-and-noted-co-instructors',
        instructorName: 'Pharrell Williams',
        enterpriseBio:
          'Grammy-winning artist teaches the importance of changing your vantage point',
        skill: 'Empathy',
        instructorHeadshotUrl: `${STATIC_IMAGE_BASE}/pw_instructor_headshot.Jpg`,
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/ayuseeq0p1dndmrmd2zt96v8ejqi',
        courseUrl:
          'https://www.masterclass.com/classes/the-power-of-empathy-with-pharrell-williams-and-noted-co-instructors',
        skillDescription: 'The Power of Empathy',
      },
      {
        id: 305,
        slug: 'black-history-black-freedom-and-black-love',
        instructorName: 'Project X',
        enterpriseBio:
          'Panel of Black thought leaders teach about race, access, and representation',
        skill: 'Cultural Awareness',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/cdv2w38fsvn2p97w6zratf071z5l',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/s9gp5hrf2a90xru4y6ho5ka81cal',
        courseUrl:
          'https://www.masterclass.com/classes/black-history-black-freedom-and-black-love',
      },
      {
        id: 386,
        slug: 'madeleine-albright-and-condoleezza-rice-teach-diplomacy',
        instructorName: 'Madeline Albright & Condolezza Rice',
        enterpriseBio:
          'Former secrataries of state teach about diplomacy and conflict resolution',
        skill: 'Cross-Cultural Communication',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/brgiu0b7l68dc0lwqwxelrvl4yrq',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/4kzim7o3abp6ic9sofd4t0tguwyi',
        courseUrl:
          'https://www.masterclass.com/classes/madeleine-albright-and-condoleezza-rice-teach-diplomacy',
      },
      {
        id: 148,
        slug: 'misty-copeland-teaches-ballet-technique-and-artistry',
        instructorName: 'Misty Copeland',
        enterpriseBio:
          'Ballet dancer teaches about advocacy for under-represented groups',
        skill: 'Empowerment',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/AbMHpMv8MhXj2eARZ3tjBVHE',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/JSnpNqo1BusYpZwAWp3HyWiw',
        courseUrl:
          'https://www.masterclass.com/classes/misty-copeland-teaches-ballet-technique-and-artistry',
      },
      {
        id: 271,
        slug: 'malala-yousafzai-teaches-creating-change',
        instructorName: 'Malala',
        enterpriseBio:
          'Nobel-prize winning activist teaches about advocacy, action, and impact',
        skill: 'Advocacy',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/khf8twpaghr25ejldj8j7x8i53ai',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/koivyt7u9tpwl82710opms0r0z5y',
        courseUrl:
          'https://www.masterclass.com/classes/malala-yousafzai-teaches-creating-change',
      },
      {
        id: 269,
        slug: 'cornel-west-teaches-philosophy',
        instructorName: 'Cornel West',
        enterpriseBio:
          'Philosopher and activist teaches about love, compassion, and connection',
        skill: 'Belonging',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/nyx8hko1uaoqahd7i735zs7zaa08',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/ulz0qrqtr10diyf927a5jgsudqdd',
        courseUrl:
          'https://www.masterclass.com/classes/cornel-west-teaches-philosophy',
      },
      {
        id: 389,
        slug: 'esther-perel-teaches-relational-intelligence',
        instructorName: 'Esther Perrel ',
        enterpriseBio:
          'Renowned psychotherapist teaches connecting with others meaningfully',
        skill: 'Emotional Intelligence',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/d2cf6y8gzvnau3et6lo99ydl1cvs',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/11ozvgl1uv33o0nifz82l7fk1oe5',
        courseUrl:
          'https://work.masterclass.com/esther-perel-teaches-relational-intelligence',
      },
    ],
  },
  {
    id: 3,
    name: 'Personal Growth',
    fullName: 'Personal Growth',
    slug: 'personal-growth',
    description:
      'Help your workforce develop the right mental habits to advance in their careers, manage stress, and find greater purpose in work and in life.',
    pageData: {
      id: 3,
      iconName: 'IconHandshake',
      audienceHeadline1:
        'For front-line workers coping with stress and burnout',
      audienceHeadline2:
        'For individual contributors looking to make a career leap',
      audienceHeadline3:
        'For leaders struggling to maintain a healthy work/life balance',
    },
    pillars: [
      {
        id: 7,
        name: 'How to learn from failure and persevere through challenges',
        samples: [
          {
            id: 13,
            title: 'Curb the self affirmations',
            description:
              'New York Times best-selling author on sales and persuasion Daniel Pink explains his idea of "buoyancy" and how to shift your internal monologue to better prepare for and overcome challenging situations.',
            skills: ['Confidence', 'Preperation'],
            course: {
              id: 198,
              slug: 'daniel-pink-teaches-sales-and-persuasion',
              headline: 'Teaches Sales and Persuasion',
              instructorName: 'Daniel Pink',
              enterpriseBio:
                'Best-selling author teaches the science of persuasion and motivating others',
              skill: 'Persuasion',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/r9Gc7uRtPAeQwZG9F7g7o64K',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/AtwHNPAexR65hmRapZyinNeM',
            },
            video16x9: {
              id: 3043,
              title: 'Curb the self affirmations',
              mediaUuid: '6264511628001',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/vsnRrP56p62M67oqVZcaq84J',
            },
          },
          {
            id: 14,
            title: 'Where does courage come from?',
            description:
              'Entrepeneur and founder of Spanx Sara Blakely explains that no one is fearless, but everyone can be courageous by being grateful for opportunities and making the journey about something bigger than yourself. ',
            skills: ['Courage', 'Resilience'],
            course: {
              id: 112,
              slug: 'sara-blakely-teaches-self-made-entrepreneurship',
              headline: 'Teaches Self-Made Entrepreneurship',
              instructorName: 'Sara Blakely',
              enterpriseBio:
                'Entrepeneur and founder of Spanx teaches how to bring a vision to market',
              skill: 'Vision',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/dNT3tuMbPkbwf4r212iaj23d',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/JXEKTHVxZbZuCQLjBqAmFgjU',
            },
            video16x9: {
              id: 5078,
              title: 'Where does courage come from?',
              mediaUuid: '6315638341112',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/34s3kyy3oscrm05syt1thpksl8jn',
            },
          },
        ],
      },
      {
        id: 8,
        name: 'How to manage stress and workload for personal and professional success',
        samples: [
          {
            id: 15,
            title: "It's a marathon, not a sprint",
            description:
              'Former Teen Vogue editor in chief explains how to avoid burn out by understanding when to say no and through self-care techniques such as "joy containers." ',
            skills: ['Work/Life Balance', 'Prioritization'],
            course: {
              id: 257,
              slug: 'elaine-welteroth-teaches-designing-your-career',
              headline: 'Teaches Designing Your Career',
              instructorName: 'Elaine Welteroth',
              enterpriseBio:
                'Former Teen Vogue editor in chief teaches strategies for career advancement',
              skill: 'Networking',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/qkbcvxgiv5xnlk6g2v9a8o16dy8j',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/lj71oti4mflogrz509oxm1e6yf50',
            },
            video16x9: {
              id: 5071,
              title: "It's a marathon, not a sprint",
              mediaUuid: '6267369597001',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/5txnrvn3lj7cw28i6reul0liynkh',
            },
          },
          {
            id: 16,
            title: "Don't fight your stress",
            description:
              "Mindfulness expert Jon Kabat-Zinn explains that there is no 'cure' for stress, but that meditation techniques can help people respond more effectively to stress triggers as opposed to merely reacting to them. ",
            skills: ['Stress Management', 'Mindfulness'],
            course: {
              id: 211,
              slug: 'jon-kabat-zinn-teaches-mindfulness-and-meditation',
              headline: 'Teaches Mindfulness and Meditation',
              instructorName: 'Jon Kabat-Zinn',
              enterpriseBio:
                'Mindfulness expert teaches how to manage stress through meditation',
              skill: 'Mindfullness',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/uqjWbdFaUKvyDyzUwxN17k9b',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/ycw7u8DYp7tDYCU9PEGrU28E',
            },
            video16x9: {
              id: 3293,
              title: "Don't fight your stress",
              mediaUuid: '6265475329001',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/q9iT2TwSB4kXgXnMEswPxR8o',
            },
          },
        ],
      },
      {
        id: 9,
        name: 'How to take the next step towards your professional goals',
        samples: [
          {
            id: 17,
            title: 'Be more than your job description',
            description:
              'Emmy-winning co-anchor of Good Morning America Robin Roberts shares strategies for showing your worth and demonstrating value beyond your job description for career advancement. ',
            skills: ['Authenticity', 'Communication'],
            course: {
              id: 189,
              slug: 'robin-roberts-teaches-effective-and-authentic-communication',
              headline: 'Teaches Effective and Authentic Communication',
              instructorName: 'Robin Roberts',
              enterpriseBio:
                'Co-anchor of Good Morning America teaches communicating value at work',
              skill: 'Communication',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/K1P5RK4cW1SZYmYehmVV7wDL',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/QocZVxY7LsHuGysDEEWDjAMY',
            },
            video16x9: {
              id: 2807,
              title: 'Be more than your job description',
              mediaUuid: '6243973668001',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/fMM5hs6xPefZp8jDm8aTfLL6',
            },
          },
          {
            id: 18,
            title: 'Your rules',
            description:
              'Former FBI hostage negotiator Chris Voss explains "tactical empathy," and why a deep understanding about the cultural norms and values of others can help you reach a more successful outcome in any situation.',
            skills: ['Negotiation', 'Empathy'],
            course: {
              id: 150,
              slug: 'chris-voss-teaches-the-art-of-negotiation',
              headline: 'Teaches the Art of Negotiation',
              instructorName: 'Chris Voss',
              enterpriseBio:
                'Former FBI hostage negotiator teaches strategies to master negotiation',
              skill: 'Negotiation',
              instructorHeadshotUrl:
                'https://www.masterclass.com/course-images/attachments/Z3fuvKirpMSKR9jT5Wh6m4Li',
              nameplateUrl:
                'https://www.masterclass.com/course-images/attachments/ktxKueUZHvrx72MyC9eE97P1',
            },
            video16x9: {
              id: 1563,
              title: 'Your rules',
              mediaUuid: '6208736923001',
              thumbnailUrl:
                'https://www.masterclass.com/course-images/attachments/faV28hfSStuwgSGPPimasrWG',
            },
          },
        ],
      },
    ],
    targetSkills: [
      {
        id: 25,
        name: 'Achieveing Success',
      },
      {
        id: 26,
        name: 'Authenticity',
      },
      {
        id: 27,
        name: 'Career Inspiration',
      },
      {
        id: 28,
        name: 'Career Lifestyle',
      },
      {
        id: 29,
        name: 'Communication',
      },
      {
        id: 3,
        name: 'Confidence',
      },
      {
        id: 4,
        name: 'Courage',
      },
      {
        id: 30,
        name: 'Curiosity',
      },
      {
        id: 6,
        name: 'Empathy',
      },
      {
        id: 31,
        name: 'Life Advice',
      },
      {
        id: 32,
        name: 'Mindfulness',
      },
      {
        id: 33,
        name: 'Negotiation',
      },
      {
        id: 34,
        name: 'Overcoming Obstacles',
      },
      {
        id: 35,
        name: 'Preparation',
      },
      {
        id: 36,
        name: 'Prioritization',
      },
      {
        id: 37,
        name: 'Resilience',
      },
      {
        id: 38,
        name: 'Stress Management',
      },
      {
        id: 39,
        name: 'Work/Life Balance',
      },
    ],
    featuredCourses: [
      {
        id: 223,
        slug: 'robin-arzon-teaches-mental-strength',
        instructorName: 'Robin Arzon',
        enterpriseBio:
          'Fitness expert teaches resilience and overcoming mental blocks',
        skill: 'Resilience',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/t592pxqdyln3o1fk7ykdo8q8bfn7',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/65ddpr3hyjqir1vjieryk4p6flvl',
        courseUrl:
          'https://work.masterclass.com/robin-arzon-teaches-mental-strength',
        skillDescription: 'Mental Strength',
      },
      {
        id: 257,
        slug: 'elaine-welteroth-teaches-designing-your-career',
        instructorName: 'Elaine Welteroth',
        enterpriseBio:
          'Former Teen Vogue editor in chief teaches strategies for career advancement',
        skill: 'Networking',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/qkbcvxgiv5xnlk6g2v9a8o16dy8j',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/lj71oti4mflogrz509oxm1e6yf50',
        courseUrl:
          'https://work.masterclass.com/elaine-welteroth-teaches-designing-your-career',
        skillDescription: 'Designing Your Career',
      },
      {
        id: 189,
        slug: 'robin-roberts-teaches-effective-and-authentic-communication',
        instructorName: 'Robin Roberts',
        enterpriseBio:
          'Co-anchor of Good Morning America teaches communicating value at work',
        skill: 'Communication',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/K1P5RK4cW1SZYmYehmVV7wDL',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/QocZVxY7LsHuGysDEEWDjAMY',
        courseUrl:
          'https://work.masterclass.com/robin-roberts-teaches-effective-and-authentic-communication',
        skillDescription: 'Effective and Authentic Communication',
      },
      {
        id: 186,
        slug: 'rupaul-teaches-self-expression-and-authenticity',
        instructorName: 'RuPaul',
        enterpriseBio:
          'Musician/model pioneer teaches about authenticity and overcoming obstacles',
        skill: 'Authenticity ',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/fJVF75ZBwtL9LThYUwDm1n35',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/k234aVrqfaNKCYLcwm3KvU7p',
        courseUrl:
          'https://www.masterclass.com/classes/rupaul-teaches-self-expression-and-authenticity',
      },
      {
        id: 207,
        slug: 'matthew-walker-teaches-the-science-of-better-sleep',
        instructorName: 'Mathew Walker',
        enterpriseBio:
          'Neuroscience professor teaches how to optmize health and productivity through better sleep',
        skill: 'Wellness',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/e2RfXT6qZzqY9guYWp2CicLs',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/TYmdWHq5fveEebZUQSwrk5yQ',
        courseUrl:
          'https://www.masterclass.com/classes/matthew-walker-teaches-the-science-of-better-sleep',
      },
      {
        id: 211,
        slug: 'jon-kabat-zinn-teaches-mindfulness-and-meditation',
        instructorName: 'Jon Kabat-Zinn',
        enterpriseBio:
          'Mindfulness expert teaches how to manage stress through meditation',
        skill: 'Mindfullness',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/uqjWbdFaUKvyDyzUwxN17k9b',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/ycw7u8DYp7tDYCU9PEGrU28E',
        courseUrl:
          'https://work.masterclass.com/jon-kabat-zinn-teaches-mindfulness-and-meditation',
      },
      {
        id: 150,
        slug: 'chris-voss-teaches-the-art-of-negotiation',
        instructorName: 'Chris Voss',
        enterpriseBio:
          'Former FBI hostage negotiator teaches strategies to master negotiation',
        skill: 'Negotiation',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/Z3fuvKirpMSKR9jT5Wh6m4Li',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/ktxKueUZHvrx72MyC9eE97P1',
        courseUrl:
          'https://work.masterclass.com/chris-voss-teaches-the-art-of-negotiation',
      },
      {
        id: 307,
        slug: 'hillary-rodham-clinton-teaches-the-power-of-resilience',
        instructorName: 'Hillary Rodham Clinton',
        enterpriseBio:
          'Former secretary of state teaches how to overcome stereotypes and bias',
        skill: 'Emotional Resilience',
        instructorHeadshotUrl:
          'https://www.masterclass.com/course-images/attachments/5twg7n6f0jnopbkvq8in6p3n3fwk',
        nameplateUrl:
          'https://www.masterclass.com/course-images/attachments/8vtiosia0qmqeo43dya35sb46e9v',
        courseUrl:
          'https://www.masterclass.com/classes/hillary-rodham-clinton-teaches-the-power-of-resilience',
      },
    ],
  },
]
